import type {
  BuyerIntent,
  MerchantQuote,
  PolicyDecision,
  ProofBundle,
  ProofEvent,
  ProofEventType,
} from "../types.js";
import { assertHash, canonicalJson, sha256 } from "./canonical.js";
import { evaluateQuote } from "./policy.js";

interface EventInput {
  type: ProofEventType;
  at: string;
  actor: ProofEvent["actor"];
  payload: unknown;
}

export async function createProofBundle(
  quote: MerchantQuote,
  intent: BuyerIntent,
  decision: PolicyDecision,
): Promise<ProofBundle> {
  const inputs: EventInput[] = [
    {
      type: "quote_received",
      at: decision.evaluatedAt,
      actor: "merchant",
      payload: { quoteHash: decision.quoteHash, quoteId: quote.quoteId },
    },
    {
      type: "intent_extracted",
      at: decision.evaluatedAt,
      actor: "agent",
      payload: { intentHash: decision.intentHash, confidence: intent.confidence },
    },
    {
      type: "policy_decided",
      at: decision.evaluatedAt,
      actor: "agent",
      payload: {
        checkoutHash: decision.checkoutHash,
        allowed: decision.allowed,
        checks: decision.checks.map(check => ({ code: check.code, passed: check.passed })),
      },
    },
  ];
  const events = await buildEventChain(inputs);
  const orderId = await sha256({
    quoteHash: decision.quoteHash,
    checkoutHash: decision.checkoutHash,
    nonce: decision.nonce,
  });
  const rootHash = events.at(-1)?.eventHash ?? (await sha256("empty"));
  return {
    version: "pacttill-proof-v1",
    orderId,
    quote,
    intent,
    decision,
    events,
    rootHash,
  };
}

export async function appendProofEvent(
  bundle: ProofBundle,
  input: EventInput,
): Promise<ProofBundle> {
  const previousHash = bundle.events.at(-1)?.eventHash ?? "0".repeat(64);
  const event = await buildEvent(bundle.events.length, previousHash, input);
  return {
    ...bundle,
    events: [...bundle.events, event],
    rootHash: event.eventHash,
  };
}

export async function verifyProofBundle(
  bundle: ProofBundle,
): Promise<{ valid: boolean; errors: string[] }> {
  const errors: string[] = [];
  try {
    assertHash(bundle.orderId, "orderId");
    assertHash(bundle.rootHash, "rootHash");
  } catch (error) {
    errors.push(error instanceof Error ? error.message : String(error));
  }
  let previousHash = "0".repeat(64);
  for (const [index, event] of bundle.events.entries()) {
    if (event.index !== index) errors.push(`event ${index} has the wrong index`);
    if (event.previousHash !== previousHash) {
      errors.push(`event ${index} does not link to the previous event`);
    }
    const expected = await sha256({
      index: event.index,
      type: event.type,
      at: event.at,
      actor: event.actor,
      payloadHash: event.payloadHash,
      previousHash: event.previousHash,
    });
    if (event.eventHash !== expected) errors.push(`event ${index} hash is invalid`);
    previousHash = event.eventHash;
  }
  if (bundle.events.length > 0 && bundle.rootHash !== previousHash) {
    errors.push("rootHash does not match the final event");
  }

  const expectedIntentHash = await sha256({
    ...bundle.intent,
    instructionVersion: 1,
  });
  if (bundle.decision.intentHash !== expectedIntentHash) {
    errors.push("intentHash does not match intent");
  }

  const expectedQuoteHash = await sha256({
    ...bundle.quote,
    quoteVersion: 1,
  });
  if (bundle.decision.quoteHash !== expectedQuoteHash) {
    errors.push("quoteHash does not match quote");
  }

  const expectedCheckoutHash = await sha256({
    intentHash: expectedIntentHash,
    quoteHash: expectedQuoteHash,
    nonce: bundle.decision.nonce,
    evaluatedAt: bundle.decision.evaluatedAt,
    decisionVersion: 1,
  });
  if (bundle.decision.checkoutHash !== expectedCheckoutHash) {
    errors.push("checkoutHash does not match the bound intent and quote");
  }

  const evaluatedAt = new Date(bundle.decision.evaluatedAt);
  if (Number.isNaN(evaluatedAt.getTime())) {
    errors.push("decision evaluatedAt is invalid");
  } else {
    const expectedDecision = await evaluateQuote(
      bundle.intent,
      bundle.quote,
      evaluatedAt,
      bundle.decision.nonce,
    );
    if (bundle.decision.allowed !== expectedDecision.allowed) {
      errors.push("decision allowed flag does not match policy");
    }
    const actualChecks = bundle.decision.checks.map(check => ({
      code: check.code,
      passed: check.passed,
    }));
    const expectedChecks = expectedDecision.checks.map(check => ({
      code: check.code,
      passed: check.passed,
    }));
    if (canonicalJson(actualChecks) !== canonicalJson(expectedChecks)) {
      errors.push("decision checks do not match policy");
    }
  }

  const expectedOrderId = await sha256({
    quoteHash: bundle.decision.quoteHash,
    checkoutHash: bundle.decision.checkoutHash,
    nonce: bundle.decision.nonce,
  });
  if (bundle.orderId !== expectedOrderId) errors.push("orderId is invalid");

  return { valid: errors.length === 0, errors };
}

async function buildEventChain(inputs: EventInput[]): Promise<ProofEvent[]> {
  const events: ProofEvent[] = [];
  let previousHash = "0".repeat(64);
  for (const [index, input] of inputs.entries()) {
    const event = await buildEvent(index, previousHash, input);
    events.push(event);
    previousHash = event.eventHash;
  }
  return events;
}

async function buildEvent(
  index: number,
  previousHash: string,
  input: EventInput,
): Promise<ProofEvent> {
  const payloadHash = await sha256(canonicalJson(input.payload));
  const eventBase = {
    index,
    type: input.type,
    at: input.at,
    actor: input.actor,
    payloadHash,
    previousHash,
  };
  return {
    ...eventBase,
    eventHash: await sha256(eventBase),
  };
}
